<template>
  <div
    class="comet-thumbnail asset-module__image asset-module__image--transparent audio-viewer"
    :class="{
      'comet-thumbnail--contain': imageSettings === 'letterbox',
    }"
  >
    <div class="comet-thumbnail__inner audio-viewer__inner">
      <div class="audio-viewer__wave-wrapper">
        <div
          v-if="!player"
          class="audio-viewer__wave-wrapper-placeholder"
        >
          <NebulaIcon
            symbol-id="asset-audio"
            size="xl"
          />
        </div>
        <div
          ref="audioViewerWave"
          :id="`audio-viewer--${id}`"
          class="audio-viewer__wave"
          :style="transforms"
        />
      </div>
      <div class="audio-viewer__controls">
        <span>
          {{ currentTime }}
        </span>
        <span>
          {{ duration }}
        </span>
        <button
          class="comet-embedded__play-button audio-viewer__play-button"
          @click="playPause"
        >
          <NebulaIcon
            :symbol-id="playing ? 'pause' : 'play'"
            class-override="comet-embedded__play-icon audio-viewer__play-icon"
            :class="{
              'audio-viewer__play-icon--paused': playing,
            }"
          />
          <span class="comet-screenreader-only">
            {{ playing ? $t('Pause') : $t('Play') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid';
import { NebulaIcon } from '@discoveryedu/nebula-components';
import { throttle } from 'lodash-es';
import { mapState } from 'pinia';
import WaveSurfer from 'wavesurfer.js';
import { tokens as cometTokens } from '@discoveryedu/comet-core/data/tokens.json';
import { bus } from '@/lib/eventBus';
import { secondsToDuration } from '@/lib/utils';
import {
  useEditorStore,
} from '@/stores';

export default {
  name: 'AudioViewer',
  components: {
    NebulaIcon,
  },
  props: {
    autoplay: Boolean,
    id: {
      type: String,
      default: () => uuid(),
    },
    imageSettings: {
      type: String,
      default: null,
    },
    scale: {
      default: 1,
      type: Number,
    },
    url: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      currentTime: null,
      duration: null,
      player: null,
      playing: false,
    };
  },
  watch: {
    autoplay() {
      this.onAutoPlayChanged();
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'editorIsFullscreen',
    ]),
    transforms() {
      // If the editor is scaled, scrubbing won't work correctly.
      return {
        transform: `scale(${1 / this.scale})`,
        width: `${this.scale * 100}%`,
        height: `${this.scale * 100}%`,
      };
    },
  },
  created() {
    this.redrawWaveform = throttle(() => {
      if (this.player && this.player.drawer) {
        this.$nextTick(() => {
          this.player.drawer.fireEvent('redraw');
        });
      }
    }, 100);
  },
  mounted() {
    bus.on('resized', this.redrawWaveform);
    bus.on('editor:zoom-changed', this.redrawWaveform);
  },
  beforeUnmount() {
    if (this.player) {
      this.player.destroy();
    }

    bus.off('resized', this.redrawWaveform);
    bus.off('editor:zoom-changed', this.redrawWaveform);
  },
  methods: {
    emitAudioProcessEvent: throttle(() => {
      bus.emit('audio:audioprocess');
    }, 1000),
    createPlayer() {
      // Prevent player from collapsing in Safari by specifying px height;
      // this will need to update manually on module/window resize (ORION-3026).
      // The default value is 128, so if this component initializes when the element
      // has zero height, use that instead
      const playerHeight = this.$refs.audioViewerWave.clientHeight || 128;

      this.player = WaveSurfer.create({
        container: `#audio-viewer--${this.id}`,
        cursorWidth: 3,
        height: playerHeight,
        hideScrollbar: true,
        progressColor: cometTokens.color.neutral['20'], // $comet-color-neutral-20
        responsive: true,
        waveColor: cometTokens.color.neutral['50'], // $comet-color-neutral-50
      });

      if (this.url) {
        this.player.load(this.url);
        this.player.on('audioprocess', () => {
          this.updateTime();
          this.emitAudioProcessEvent();
        });
        this.player.on('ready', () => {
          this.updateTime();
          this.onAutoPlayChanged();
        });
        this.player.on('seek', this.updateTime);
        this.player.on('play', () => {
          this.playing = true;
        });
        this.player.on('pause', () => {
          this.playing = false;
        });
      }
    },
    onAutoPlayChanged() {
      if (this.autoplay && this.editorIsFullscreen && this.player?.isReady) {
        this.$nextTick(() => {
          // Play from beginning.
          this.reset();
          this.player.play();
        });
      }
    },
    playPause() {
      if (!this.player) {
        this.createPlayer();
        this.player.on('ready', () => {
          this.player.playPause();
        });
      } else {
        this.player.playPause();
      }
    },
    pause() {
      if (this.player) {
        this.player.pause();
      }
    },
    reset() {
      if (this.player) {
        // Reset player to beginning.
        this.player.setCurrentTime(0);
      }
    },
    updateTime() {
      this.currentTime = secondsToDuration(this.player.getCurrentTime());
      this.duration = secondsToDuration(this.player.getDuration());
    },
  },
};
</script>

<style lang="stylus">
.audio-viewer {
  &__inner { // .audio-viewer__inner
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  &__wave-wrapper {
    flex-grow: 1;
    flex-shrink: 1;
    min-height: 0;
    position: relative;
  }

  &__wave-wrapper-placeholder {
    align-items: center;
    background-color: $nebula-color-interface-blue-400;
    bottom: $nebula-space-1x;
    display: flex;
    justify-content: center;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;

    svg.nebula-icon {
      fill: $nebula-color-white;
    }
  }

  &__wave { // .audio-viewer__wave
    bottom: 0;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    transform-origin: top left;
    z-index: 100; // Above drag handle so audio wave interface is not partially covered.

    > * {
      // Override inline WaveSurfer style, which doesn't update responsively
      height: 100% !important;
    }
  }

  &__controls { // .audio-viewer__controls
    align-items: center;
    display: flex;
    flex-shrink: 0;
    height: $nebula-space-8x; // match play button dimensions
    justify-content: space-between;
    position: relative;
  }

  &__play-button { // .audio-viewer__play-button
    border: none;
    cursor: pointer;
    padding: 0;
  }

  &__play-icon {
    left: 50%;
  }
}
</style>
